import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  FileText, Video, BookOpen, FileSpreadsheet, ArrowLeft, 
  Clock, Eye, ExternalLink, Share2, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const CONTENT_TYPES = {
  article: { label: 'Article', icon: FileText },
  video: { label: 'Video', icon: Video },
  guide: { label: 'Guide', icon: BookOpen },
  worksheet: { label: 'Worksheet', icon: FileSpreadsheet },
};

function ContentDetail() {
  const { contentId } = useParams();
  const { user, logout } = useAuth();
  const [content, setContent] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (contentId) {
      fetchContent();
    }
  }, [contentId]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/content/${contentId}`);
      setContent(response.data.content);
      setRelated(response.data.related || []);
    } catch (error) {
      setError('Content not found');
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: content?.title,
          text: content?.description,
          url: url
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  const getVideoEmbed = (videoUrl) => {
    if (!videoUrl) return null;
    
    // YouTube
    const youtubeMatch = videoUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (youtubeMatch) {
      return (
        <iframe
          className="w-full aspect-video rounded-lg"
          src={`https://www.youtube.com/embed/${youtubeMatch[1]}`}
          title={content?.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }
    
    // Vimeo
    const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return (
        <iframe
          className="w-full aspect-video rounded-lg"
          src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
          title={content?.title}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      );
    }
    
    // Loom
    const loomMatch = videoUrl.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loomMatch) {
      return (
        <iframe
          className="w-full aspect-video rounded-lg"
          src={`https://www.loom.com/embed/${loomMatch[1]}`}
          title={content?.title}
          frameBorder="0"
          allowFullScreen
        />
      );
    }
    
    // If it's an embed code, render it directly
    if (videoUrl.includes('<iframe')) {
      return <div dangerouslySetInnerHTML={{ __html: videoUrl }} />;
    }
    
    // Fallback: just show a link
    return (
      <div className="p-8 bg-muted rounded-lg text-center">
        <Video className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
        <Button onClick={() => window.open(videoUrl, '_blank')}>
          <ExternalLink className="w-4 h-4 mr-2" />
          Open Video
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="font-semibold text-xl mb-2">Content Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The content you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <Button onClick={() => window.location.href = '/library'}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Library
          </Button>
        </Card>
      </div>
    );
  }

  const TypeIcon = CONTENT_TYPES[content.contentType]?.icon || FileText;

  return (
    <div>
      <div className="py-2">
        {/* Back Button */}
        <Button variant="ghost" className="mb-6" onClick={() => window.location.href = '/library'}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Library
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <TypeIcon className="w-5 h-5 text-primary" />
                <span className="text-sm text-muted-foreground capitalize">
                  {CONTENT_TYPES[content.contentType]?.label}
                </span>
                {content.readTime && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {content.readTime}
                    </span>
                  </>
                )}
                {content.duration && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {content.duration}
                    </span>
                  </>
                )}
              </div>
              
              <h1 className="h1 text-3xl mb-4">{content.title}</h1>
              <p className="text-lg text-muted-foreground mb-4">{content.description}</p>
              
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {content.tags?.fearClusters?.map(tag => (
                  <Badge key={tag} variant="outline">{tag}</Badge>
                ))}
                {content.tags?.archetypes?.map(tag => (
                  <Badge key={tag} variant="outline">{tag}</Badge>
                ))}
                {content.tags?.topics?.map(tag => (
                  <Badge key={tag} variant="secondary">{tag}</Badge>
                ))}
              </div>
              
              {/* Stats & Actions */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {content.viewCount} views
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleShare}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  {content.externalUrl && (
                    <Button variant="outline" size="sm" onClick={() => window.open(content.externalUrl, '_blank')}>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open External
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Content Body */}
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                {/* Video Content */}
                {content.contentType === 'video' && (
                  <div className="p-4">
                    {getVideoEmbed(content.content)}
                  </div>
                )}
                
                {/* Article/Guide Content */}
                {(content.contentType === 'article' || content.contentType === 'guide') && (
                  <div className="p-6 prose prose-slate max-w-none">
                    <ReactMarkdown
                      components={{
                        h1: ({node, ...props}) => <h1 className="text-2xl font-bold mt-8 mb-4" {...props} />,
                        h2: ({node, ...props}) => <h2 className="text-xl font-semibold mt-6 mb-3" {...props} />,
                        h3: ({node, ...props}) => <h3 className="text-lg font-medium mt-4 mb-2" {...props} />,
                        p: ({node, ...props}) => <p className="mb-4 leading-relaxed" {...props} />,
                        ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
                        ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
                        li: ({node, ...props}) => <li className="leading-relaxed" {...props} />,
                        blockquote: ({node, ...props}) => (
                          <blockquote className="border-l-4 border-primary pl-4 italic my-4 text-muted-foreground" {...props} />
                        ),
                        code: ({node, inline, ...props}) => 
                          inline 
                            ? <code className="bg-muted px-1.5 py-0.5 rounded text-sm" {...props} />
                            : <code className="block bg-muted p-4 rounded-lg overflow-x-auto text-sm" {...props} />,
                        a: ({node, ...props}) => (
                          <a className="text-primary hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
                        ),
                      }}
                    >
                      {content.content}
                    </ReactMarkdown>
                  </div>
                )}
                
                {/* Worksheet Content */}
                {content.contentType === 'worksheet' && (
                  <div className="p-6">
                    <div className="prose prose-slate max-w-none mb-6">
                      <ReactMarkdown>{content.content}</ReactMarkdown>
                    </div>
                    {content.externalUrl && (
                      <Button onClick={() => window.open(content.externalUrl, '_blank')}>
                        <Download className="w-4 h-4 mr-2" />
                        Download Worksheet
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Related Content */}
            {related.length > 0 && (
              <div>
                <h3 className="font-semibold mb-4">Related Resources</h3>
                <div className="space-y-3">
                  {related.map(item => {
                    const RelatedIcon = CONTENT_TYPES[item.contentType]?.icon || FileText;
                    return (
                      <Card 
                        key={item.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => window.location.href = `/library/${item.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-muted rounded">
                              <RelatedIcon className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm line-clamp-2 mb-1">
                                {item.title}
                              </h4>
                              <p className="text-xs text-muted-foreground capitalize">
                                {CONTENT_TYPES[item.contentType]?.label}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* CTA */}
            <Card className="mt-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold mb-2">Ready to take action?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Book a session to discuss how to apply these insights.
                </p>
                <Button onClick={() => window.location.href = '/book'} className="w-full">
                  Book a Session
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContentDetail;
